import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Building, MapPin, Phone, Mail, Plus, Search, X, Edit } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Properties() {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingProperty, setEditingProperty] = useState(null);
  const [formData, setFormData] = useState({
    name: '', legal_name: '', property_type: 'residential',
    address: '', city: '', province: 'ON', postal_code: '',
    phone: '', email: '', unit_count: 0
  });

  useEffect(() => { fetchProperties(); }, []);

  const fetchProperties = async () => {
    try {
      const response = await axios.get(`${API}/corporations`);
      setProperties(response.data);
    } catch (error) { console.error('Failed to fetch properties:', error); }
    finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingProperty) {
        await axios.put(`${API}/corporations/${editingProperty.id}`, formData);
      } else {
        await axios.post(`${API}/corporations`, formData);
      }
      setShowModal(false);
      resetForm();
      fetchProperties();
    } catch (error) { console.error('Failed to save property:', error); }
  };

  const openEditModal = (property) => {
    setEditingProperty(property);
    setFormData({
      name: property.name || '', legal_name: property.legal_name || '',
      property_type: property.property_type || 'residential',
      address: property.address || '', city: property.city || '',
      province: property.province || 'ON', postal_code: property.postal_code || '',
      phone: property.phone || '', email: property.email || '',
      unit_count: property.unit_count || 0
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setEditingProperty(null);
    setFormData({ name: '', legal_name: '', property_type: 'residential', address: '', city: '', province: 'ON', postal_code: '', phone: '', email: '', unit_count: 0 });
  };

  const filteredProperties = properties.filter(p =>
    p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.city?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getTypeColor = (type) => {
    switch (type) {
      case 'condo': return 'bg-indigo-100 text-indigo-700';
      case 'residential': return 'bg-emerald-100 text-emerald-700';
      case 'commercial': return 'bg-violet-100 text-violet-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div></div>;
  }

  return (
    <div className="space-y-6" data-testid="properties-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Properties</h1>
          <p className="text-slate-500">Manage your property portfolio</p>
        </div>
        <button onClick={() => { resetForm(); setShowModal(true); }} className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-indigo-600 to-violet-600 text-white rounded-lg hover:from-indigo-700 hover:to-violet-700" data-testid="add-property-btn">
          <Plus className="w-4 h-4" /> Add Property
        </button>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
        <input type="text" placeholder="Search properties..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredProperties.map((property) => (
          <div key={property.id} className="bg-white rounded-xl border border-slate-200 overflow-hidden hover:shadow-lg transition-shadow">
            <div className="bg-gradient-to-r from-indigo-600 to-violet-600 p-4">
              <div className="flex items-start justify-between">
                <div className="bg-white/20 p-2 rounded-lg"><Building className="w-6 h-6 text-white" /></div>
                <div className="flex items-center gap-2">
                  <span className={`text-xs px-2 py-1 rounded-full ${getTypeColor(property.property_type)}`}>{property.property_type}</span>
                  <button onClick={() => openEditModal(property)} className="p-1 bg-white/20 rounded hover:bg-white/30"><Edit className="w-4 h-4 text-white" /></button>
                </div>
              </div>
              <h3 className="text-lg font-semibold text-white mt-3">{property.name}</h3>
            </div>
            <div className="p-4 space-y-3">
              {property.address && <div className="flex items-center gap-2 text-sm text-slate-600"><MapPin className="w-4 h-4 text-slate-400" />{property.address}, {property.city}, {property.province}</div>}
              {property.phone && <div className="flex items-center gap-2 text-sm text-slate-600"><Phone className="w-4 h-4 text-slate-400" />{property.phone}</div>}
              {property.email && <div className="flex items-center gap-2 text-sm text-slate-600"><Mail className="w-4 h-4 text-slate-400" />{property.email}</div>}
              <div className="grid grid-cols-2 gap-4 pt-4 border-t border-slate-100">
                <div><p className="text-2xl font-bold text-slate-900">{property.unit_count || 0}</p><p className="text-xs text-slate-500">Total Units</p></div>
                {property.property_type === 'condo' && property.reserve_fund_balance && <div><p className="text-2xl font-bold text-emerald-600">${(property.reserve_fund_balance / 1000000).toFixed(1)}M</p><p className="text-xs text-slate-500">Reserve Fund</p></div>}
              </div>
              <Link to={`/properties/${property.id}`} className="block w-full text-center py-2 mt-4 border border-indigo-200 text-indigo-600 rounded-lg hover:bg-indigo-50">View Details</Link>
            </div>
          </div>
        ))}
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-900">{editingProperty ? 'Edit Property' : 'Add Property'}</h3>
              <button onClick={() => setShowModal(false)} className="p-1 hover:bg-slate-100 rounded"><X className="w-5 h-5 text-slate-500" /></button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Property Name</label>
                <input type="text" required value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} className="w-full px-3 py-2 border border-slate-300 rounded-lg" placeholder="Property name" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Legal Name</label>
                  <input type="text" value={formData.legal_name} onChange={(e) => setFormData({...formData, legal_name: e.target.value})} className="w-full px-3 py-2 border border-slate-300 rounded-lg" placeholder="Legal entity name" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Type</label>
                  <select value={formData.property_type} onChange={(e) => setFormData({...formData, property_type: e.target.value})} className="w-full px-3 py-2 border border-slate-300 rounded-lg">
                    <option value="residential">Residential</option>
                    <option value="condo">Condo</option>
                    <option value="commercial">Commercial</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Address</label>
                <input type="text" value={formData.address} onChange={(e) => setFormData({...formData, address: e.target.value})} className="w-full px-3 py-2 border border-slate-300 rounded-lg" placeholder="Street address" />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">City</label>
                  <input type="text" value={formData.city} onChange={(e) => setFormData({...formData, city: e.target.value})} className="w-full px-3 py-2 border border-slate-300 rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Province</label>
                  <input type="text" value={formData.province} onChange={(e) => setFormData({...formData, province: e.target.value})} className="w-full px-3 py-2 border border-slate-300 rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Postal Code</label>
                  <input type="text" value={formData.postal_code} onChange={(e) => setFormData({...formData, postal_code: e.target.value})} className="w-full px-3 py-2 border border-slate-300 rounded-lg" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Phone</label>
                  <input type="tel" value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} className="w-full px-3 py-2 border border-slate-300 rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                  <input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} className="w-full px-3 py-2 border border-slate-300 rounded-lg" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Number of Units</label>
                <input type="number" min="0" value={formData.unit_count} onChange={(e) => setFormData({...formData, unit_count: parseInt(e.target.value) || 0})} className="w-full px-3 py-2 border border-slate-300 rounded-lg" placeholder="Total units in property" />
                <p className="text-xs text-slate-500 mt-1">You can add individual units in the Units module after creating the property</p>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-50">Cancel</button>
                <button type="submit" className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">{editingProperty ? 'Save Changes' : 'Add Property'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
